// src/components/handshake.js

/*
  The Handshake component acts as a shared interface for displaying and editing 
  event summary fields, seamlessly adapting between read-only mode in the viewer 
  and editable mode in the editor using a single, reusable layout.

  - Displays event details like Description, Location, Date, Weather, and more.
  - Supports two modes:
      * View mode: Shows event info as plain text and links.
      * Edit mode: Shows input fields for hosts/editors to update info.
  - Used in both the event viewer (read-only) and event editor (editable).
  - In viewer mode, it shows a simple single-column layout.
  - In editor mode, it allows inline editing of event details.
  - If you want a multi-column layout, replace this component in the viewer,
    but keep it as-is in the editor for editing functionality.
*/

import React, { useState, useEffect } from 'react';

export default function Handshake({ event, setEvent, isEditor = false, showHeading = true, overrideDateField, setShowLinkModal}) {
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [customLabel1, setCustomLabel1] = useState('');
  const [customLabel2, setCustomLabel2] = useState('');

useEffect(() => {
  setCustomLabel1(event?.custom_field_1_label ?? '');
  setCustomLabel2(event?.custom_field_2_label ?? '');
}, [event.custom_field_1_label, event.custom_field_2_label]);

  const handleChange = (key, value) => {
    if (value.length <= 40) {
      setEvent((prev) => ({ ...prev, [key]: value }));
    } else {
      setShowLimitModal(true);
    }
  };

  const handleLabelChange = (labelKey, value) => {
    const limited = value.slice(0, 30);
    setEvent((prev) => ({ ...prev, [labelKey]: limited }));
    if (labelKey === 'custom_field_1_label') setCustomLabel1(limited);
    if (labelKey === 'custom_field_2_label') setCustomLabel2(limited);
  };

  const fields = [
    { key: 'description', label: 'Description' },
    { key: 'event_location', label: 'Location' },
    { key: 'event_date', label: 'Date' },
    { key: 'weather_link', label: 'Weather' },
    { key: 'what_to_bring', label: 'What to Bring' },
    { key: 'event_link', label: 'Event Hyperlinks' },
    { key: 'moderator_contact', label: 'Moderator Contact' },
    { key: 'custom_field_1', label: customLabel1 || 'Custom Field 1' },
    { key: 'custom_field_2', label: customLabel2 || 'Custom Field 2' },
  ];

  const renderModal = () => (
    <div className="modal-overlay" onClick={() => setShowLimitModal(false)}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h3>Too Many Characters</h3>
        <p>There's a 40 character limit. For additional details, please use the Event Details Box.</p>
        <button onClick={() => setShowLimitModal(false)}>OK</button>
      </div>
    </div>
  );

  const visibleFields = fields.filter(({ key }) => {
  const isCustom = key === 'custom_field_1' || key === 'custom_field_2';
  return isCustom ? event[key]?.trim() || isEditor : true;
});

  return (
  <div className="static-details three-column">
    {showHeading && <h3><u>Event Summary</u></h3>}
    <div className="columns-wrapper">
      {[0, 1, 2].map((colIndex) => (
        <div className="column" key={colIndex}>
          {visibleFields
            .filter((_, i) => i % 3 === colIndex)
            .map(({ key, label }) => {
              if (key === 'event_date') {
                return overrideDateField ? (
                  <React.Fragment key="event_date_override">{overrideDateField}</React.Fragment>
                ) : (
                  <div key={key} className="field-block">
                    <label className="field-label"><b>Date:</b></label>
                    {isEditor ? (
                      <input
                        value={event[key] || ''}
                        onChange={(e) => handleChange(key, e.target.value)}
                        placeholder="Enter date..."
                      />
                    ) : (
                      <div style={{ marginTop: '4px' }}>
                        {event[key] && !isNaN(new Date(event[key]))
                          ? new Date(event[key]).toLocaleDateString(undefined, {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })
                          : 'TBD'}
                      </div>
                    )}
                  </div>
                );
              }

              const isCustom = key === 'custom_field_1' || key === 'custom_field_2';
              const labelKey = key === 'custom_field_1' ? 'custom_field_1_label' : 'custom_field_2_label';

              return (
                <div key={key} className="field-block">
                  <label className="field-label">
                    {isEditor && isCustom ? (
                      <input
                        className="editable-label"
                        type="text"
                        value={key === 'custom_field_1' ? customLabel1 : customLabel2}
                        onChange={(e) => handleLabelChange(labelKey, e.target.value)}
                        placeholder=" Custom Label"
                      />
                    ) : (
                      <b>{label}:</b>
                    )}
                  </label>

                  {isEditor ? (
                    key === 'event_link' ? (
                      <input
                        type="text"
                        readOnly
                        className="date-input"
                        value={
                          Array.isArray(event[key])
                            ? `${event[key].length} link${event[key].length !== 1 ? 's' : ''}`
                            : 'No links added'
                        }
                        placeholder="Click to edit links…"
                        onClick={() => setShowLinkModal(true)}
                      />
                    ) : (
                      <input
                        type="text"
                        value={event[key] || ''}
                        onChange={(e) => handleChange(key, e.target.value)}
                        placeholder={`Enter ${label.toLowerCase()}...`}
                      />
                    )
                  ) : key === 'event_link' ? (
                    <div className="link-display-box">
                      {Array.isArray(event[key]) && event[key].length > 0 ? (
                        <ul className="link-list">
                          {event[key].map((link, idx) => (
                            <li key={idx}>
                              <a
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={link.url}
                              >
                                {link.label}
                              </a>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div style={{ color: '#666', fontStyle: 'italic' }}>Links TBD</div>
                      )}
                    </div>
                  ) : (
                    <div>{event[key]}</div>
                  )}
                </div>
              );
            })}
        </div>
      ))}
    </div>

    {showLimitModal && renderModal()}
  </div>
);
}